import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Table, Thead, Tbody, Tr, Th, Td, useToast, Box, Heading, Flex, Select, Input, Button, Spinner, IconButton, useTheme } from "@chakra-ui/react";
import { FaTrash, FaEdit, FaSave } from "react-icons/fa";
import AddRegionStore from "./AddRegionStore"; // make sure the path is correct

const RegionsTable = () => {
  const toast = useToast();
  const [filteredData, setFilteredData] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState("");
  // Two values, deliberately: `searchInput` is what the user has typed and
  // repaints on every keystroke; `searchTerm` is the debounced copy and is the
  // only one the fetch depends on. Typing "PNP001" used to fire twelve server
  // round trips — one per keystroke, doubled because searchTerm sat in two
  // effect dependency arrays.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editRowData, setEditRowData] = useState({ site_code: "", region: "" });

  const [retailers, setRetailers] = useState(["", "PNP", "CHK", "DIS", "CLI", "MAK"]);
  const [selectedRetailer, setSelectedRetailer] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const theme = useTheme();

  // Discards a response whose request has already been superseded. With several
  // filters in flight at once a slower earlier reply could otherwise land last
  // and overwrite newer results — the "I kept typing and the wrong list came
  // back" symptom.
  const reqIdRef = useRef(0);

  const fetchData = async (page = 1) => {
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-store-regions`, { page, limit: 50, search: searchTerm, selectedRegion, selectedRetailer });
      if (myReq !== reqIdRef.current) return; // a newer request is already out
      setFilteredData(response.data.data);
      setRegions(response.data.regions);
      setTotalPages(Math.ceil(response.data.total / 50));
      setCurrentPage(page);
      setIsLoading(false);
    } catch (error) {
      if (myReq !== reqIdRef.current) return;
      toast({
        title: "Failed to load data",
        description: "Technical error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsLoading(false);
    }
  };

  // Hold the keystrokes, then publish once. Clearing the timer on every change
  // is what collapses a burst of typing into a single request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      // Paging back to 1 happens here, in the same update as the new term, so
      // React batches them into one render and one fetch. Doing it in a
      // separate effect fired a wasted request against the OLD page number
      // first.
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The single fetch. This replaced four overlapping effects that between them
  // fired four identical requests on mount and two per keystroke.
  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, searchTerm, selectedRegion, selectedRetailer]);

  const deleteStore = async (site_code, site_description) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/delete-region-store`, {
        site_code,
        site_description,
      });
      toast({
        title: "Store deleted",
        description: "The store has been deleted successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      fetchData(currentPage);
    } catch (error) {
      toast({
        title: "Failed to delete store",
        description: "An error occurred while deleting the store.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEdit = (index) => {
    setEditRowIndex(index);
    setEditRowData({
      site_code: filteredData[index].site_code,
      region: filteredData[index].region,
      site_description: filteredData[index].site_description,
      retailer: filteredData[index].retailer,
    });
  };

  const handleSave = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/admin/upd-region-store`, {
        site_code: editRowData.site_code,
        region: editRowData.region,
        site_description: editRowData.site_description,
        retailer: editRowData.retailer,
      });
      toast({
        title: "Store updated",
        description: "The store has been updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEditRowIndex(null);
      fetchData(currentPage);
    } catch (error) {
      toast({
        title: "Failed to update store",
        description: "An error occurred while updating the store.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }).format(date);
    } catch (error) {
      console.log(dateString, error);
      return "err";
    }
  };

  return (
    <Flex minWidth="75vw">
      <Box flex="1" maxHeight="700px" overflowY="auto">
        <Heading size="md" textAlign="center" mb={4} bg={theme.colors.primary2} color={"white"} p={1}>
          Store Regions
        </Heading>
        {isLoading ? (
          <Spinner />
        ) : (
          <Table variant="striped" size="sm" colorScheme="custom" border="1px" borderColor="gray.200" borderRadius="md">
            <Thead>
              <Tr>
                <Th>Site Code</Th>
                <Th>Site Name</Th>
                <Th>Region</Th>
                <Th>Retailer</Th>
                <Th>Created Date</Th>
                <Th>Delete</Th>
                <Th>Edit</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.map((row, index) => (
                <Tr key={index}>
                  <Td>{row.site_code}</Td>

                  <Td>{editRowIndex === index ? <Input value={editRowData.site_description} onChange={(e) => setEditRowData({ ...editRowData, site_description: e.target.value })} /> : row.site_description}</Td>

                  <Td>{editRowIndex === index ? <Input value={editRowData.region} onChange={(e) => setEditRowData({ ...editRowData, region: e.target.value })} /> : row.region}</Td>

                  <Td>
                    {editRowIndex === index ? (
                      <Select value={editRowData.retailer} onChange={(e) => setEditRowData({ ...editRowData, retailer: e.target.value })}>
                        {retailers.map((retailer, idx) => (
                          <option key={idx} value={retailer}>
                            {retailer}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      row.retailer
                    )}
                  </Td>

                  <Td>{formatDate(row.createddate)}</Td>
                  <Td>
                    <IconButton icon={<FaTrash />} onClick={() => deleteStore(row.site_code, row.site_description)} colorScheme="red" variant="ghost" />{" "}
                  </Td>
                  <Td>{editRowIndex === index ? <IconButton icon={<FaSave />} onClick={handleSave} colorScheme="green" variant="ghost" /> : <IconButton icon={<FaEdit />} onClick={() => handleEdit(index)} colorScheme="blue" variant="ghost" />}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <Flex flexDirection={"column"} gap={"4px"}>
        <Box ml={4}>
          <Select placeholder="Select Region" mb={4} value={selectedRegion} onChange={(e) => { setSelectedRegion(e.target.value); setCurrentPage(1); }}>
            {regions.map((region, index) => (
              <option key={index} value={region}>
                {region}
              </option>
            ))}
          </Select>
          <Select placeholder="Select Retailer" mb={4} value={selectedRetailer} onChange={(e) => { setSelectedRetailer(e.target.value); setCurrentPage(1); }}>
            {retailers.map((retailer, index) => (
              <option key={index} value={retailer}>
                {retailer}
              </option>
            ))}
          </Select>
          <Input placeholder="Search site code or name" mb={4} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <Button colorScheme="blue" onClick={() => setIsModalOpen(true)}>
            Add New Store
          </Button>
        </Box>
        <Box mt={4} display="flex" justifyContent="center">
          <Button onClick={() => fetchData(currentPage - 1)} isDisabled={currentPage === 1} mr={2}>
            Previous
          </Button>
          <Button onClick={() => fetchData(currentPage + 1)} isDisabled={currentPage === totalPages}>
            Next
          </Button>
        </Box>
      </Flex>

      <AddRegionStore isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} regions={regions} retailers={retailers} refreshData={fetchData} />
    </Flex>
  );
};

export default RegionsTable;
